import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import MovieIcon from '@mui/icons-material/Movie';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

type ThumbnailSize = 'small' | 'wide' | 'large';

interface OvVideoThumbnailProps {
  src: string | null;
  size?: ThumbnailSize;
  alt?: string;
  placeholderLabel?: string;
  isDimmed?: boolean;
}

const SIZES: Record<ThumbnailSize, { width: string; height: string }> = {
  small: { width: '4rem', height: '4rem' },
  wide: { width: '8.5rem', height: '4.78125rem' },
  large: { width: '100%', height: '12rem' },
};

const OvVideoThumbnail: FC<OvVideoThumbnailProps> = ({
  src,
  size = 'small',
  alt = '',
  placeholderLabel,
  isDimmed = false,
}) => {
  const { t } = useTranslation();

  if (src) {
    return (
      <Image src={src} alt={alt} style={SIZES[size]} $isDimmed={isDimmed} />
    );
  }

  return (
    <Placeholder style={SIZES[size]} $isDimmed={isDimmed}>
      <MovieIcon aria-hidden="true" />
      <span>{placeholderLabel ?? t('videos.noPoster')}</span>
    </Placeholder>
  );
};

export default OvVideoThumbnail;

const Image = styled.img<{ $isDimmed: boolean }>`
  display: block;
  flex: none;
  object-fit: contain;
  border-radius: ${Variables.borderRadius.MEDIUM};
  background-color: ${Colours.OV_BASE};
  ${({ $isDimmed }) => $isDimmed && 'opacity: 0.5;'}
`;

const Placeholder = styled.span<{ $isDimmed: boolean }>`
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.125rem;
  padding: 0.25rem;
  box-sizing: border-box;
  border-radius: ${Variables.borderRadius.MEDIUM};
  background-color: ${Colours.OV_DISABLED};
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
  line-height: 1;
  text-align: center;
  ${({ $isDimmed }) => $isDimmed && 'opacity: 0.5;'}
`;
